import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { ActivityAsyncAdapter } from './activity.adapter';
import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { ReferenceAdapter } from '../reference.adapter';
import { Notebook, BigDataPool, SynapseNotebookActivity, AzureSynapseArtifactsLinkedService, A365BackendModel, SparkConfiguration, Credential } from 'shell-models-lib';
import { toParameterObject, toParameterClass } from '../linkedservices/parameter-config.util';
import { convertParameterValue, convertParametersToModel } from 'app/shared/utils/parameter-converter.util';
import { isString } from 'app/shared/utils/string.util';
import { ValueAdapter } from '../value.adapter';
import { GlobalVariables } from 'app/services/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SynapseNotebookActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            notebook: '*',
            parameters: '*',
            snapshot: '*',
            sparkPool: '*',
            executorSize: '*',
            conf: '*',
            driverSize: '*',
            numExecutors: '*',
            authentication: '*',
            configurationType: GlobalVariables.enableNotebookSparkConfigOverride ? '*' : undefined,
            targetSparkConfiguration: GlobalVariables.enableNotebookSparkConfigOverride ? '*' : undefined,
            sparkConfig: GlobalVariables.enableNotebookSparkConfigOverride ? '*' : undefined,
        }
    }
})
export class SynapseNotebookActivityAdapter extends ActivityAsyncAdapter<SynapseNotebookActivity, A365BackendModel.SynapseNotebookActivity> {

    protected async _toResource(activity: SynapseNotebookActivity): Promise<A365BackendModel.SynapseNotebookActivity> {
        const modelSparkNotebookActivity = super._getCommonActivityProperties(
            activity, Metamodel.modelFor(SynapseNotebookActivity).name) as A365BackendModel.SynapseNotebookActivity;

        modelSparkNotebookActivity.typeProperties = {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            notebook: this._getNotebook(activity),
            parameters: toParameterObject(activity.parameters, true),
            snapshot: activity.snapshot,
            sparkPool: this._getSparkpool(activity),
            executorSize: ValueAdapter.toResource(activity, SynapseNotebookActivity.EXECUTOR_SIZE),
            conf: {
                'spark.dynamicAllocation.enabled': ValueAdapter.toResource(activity, SynapseNotebookActivity.DYNAMIC_EXECUTOR),
                'spark.dynamicAllocation.minExecutors': ValueAdapter.toResource(activity, SynapseNotebookActivity.MIN_EXECUTORS),
                'spark.dynamicAllocation.maxExecutors': activity.dynamicExecutor || activity.dynamicExecutor === undefined ?
                                                        ValueAdapter.toResource(activity, SynapseNotebookActivity.MAX_EXECUTORS) :
                                                        ValueAdapter.toResource(activity, SynapseNotebookActivity.MIN_EXECUTORS)
            },
            driverSize: ValueAdapter.toResource(activity, SynapseNotebookActivity.DRIVER_SIZE),
            numExecutors: ValueAdapter.toResource(activity, SynapseNotebookActivity.MIN_EXECUTORS)
        };

        if (modelSparkNotebookActivity.typeProperties.parameters) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            for (const parameterName in modelSparkNotebookActivity.typeProperties.parameters) {
                const parameter = modelSparkNotebookActivity.typeProperties.parameters[parameterName];
                if (parameter.type === 'bool' && isString(parameter.value)) {
                    parameter.value = convertParameterValue(parameter.value, parameter.type, 'SynapseNotebookActivityAdapter',
                    this.ensureLogger(), true /* don't parse as an object */);
                }
            }
        }

        if (activity.authentication?.type) {
            modelSparkNotebookActivity.typeProperties.authentication = {};
            modelSparkNotebookActivity.typeProperties.authentication.type = activity.authentication.type;
            if (activity.authentication.type === 'UserAssignedManagedIdentity' && activity.authentication.credential){
                modelSparkNotebookActivity.typeProperties.authentication.credential = ReferenceAdapter.toResource(activity.authentication.credential, this.context) as Model.CredentialReference
            }
        }

        if (GlobalVariables.enableNotebookSparkConfigOverride) {
            modelSparkNotebookActivity.typeProperties.configurationType = activity.configurationType;
            modelSparkNotebookActivity.typeProperties.targetSparkConfiguration = this._getSparkConfiguration(activity);
            modelSparkNotebookActivity.typeProperties.sparkConfig = toParameterObject(activity.sparkConfig);
        }

        if (activity.linkedService) {
            modelSparkNotebookActivity.linkedServiceName = this._toLinkedServiceReferenceModel(activity.linkedService, activity.linkedServiceParameters);
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelSparkNotebookActivity, parsed);
        }

        return modelSparkNotebookActivity;
    }

    protected async _toModel(modelActivity: A365BackendModel.SynapseNotebookActivity): Promise<SynapseNotebookActivity> {
        const activity: SynapseNotebookActivity = SynapseNotebookActivity.MODEL.create<SynapseNotebookActivity>(modelActivity.name, modelActivity.description);
        this._updatedCommonProperties(modelActivity, activity);

        const notebook = modelActivity.typeProperties?.notebook;
        if (notebook != null) {
            if (typeof notebook.referenceName === 'string') {
                activity.notebook = ReferenceAdapter.toProxyModel<Notebook>(
                    Metamodel.modelFor(Notebook),
                    notebook, this.context);
            } else {
                ValueAdapter.toModel(activity, SynapseNotebookActivity.NAME, notebook.referenceName);
            }
        }

        const sparkPool = modelActivity.typeProperties?.sparkPool;
        if (sparkPool != null) {
            if (typeof sparkPool.referenceName === 'string') {
                activity.sparkPool = ReferenceAdapter.toProxyModel<BigDataPool>(
                    Metamodel.modelFor(BigDataPool),
                    sparkPool, this.context);
            } else {
                ValueAdapter.toModel(activity, SynapseNotebookActivity.SPARKPOOL, sparkPool.referenceName);
            }
        }

        activity.parameters = toParameterClass(modelActivity.typeProperties.parameters, activity, true);
        activity.snapshot = modelActivity.typeProperties.snapshot;

        if (modelActivity.typeProperties) {
            ValueAdapter.toModel(activity, SynapseNotebookActivity.EXECUTOR_SIZE, modelActivity.typeProperties.executorSize);
            ValueAdapter.toModel(activity, SynapseNotebookActivity.DRIVER_SIZE, modelActivity.typeProperties.driverSize);
            ValueAdapter.toModel(activity, SynapseNotebookActivity.DYNAMIC_EXECUTOR, modelActivity.typeProperties.conf ?
                                 modelActivity.typeProperties.conf['spark.dynamicAllocation.enabled'] : null);
            ValueAdapter.toModel(activity, SynapseNotebookActivity.MIN_EXECUTORS, modelActivity.typeProperties.conf ?
                                 modelActivity.typeProperties.conf['spark.dynamicAllocation.minExecutors'] : null);
            ValueAdapter.toModel(activity, SynapseNotebookActivity.MAX_EXECUTORS, modelActivity.typeProperties.conf ?
                                 modelActivity.typeProperties.conf['spark.dynamicAllocation.maxExecutors'] : null);

            if (modelActivity.typeProperties.authentication) {
                activity.authentication.type = modelActivity.typeProperties.authentication.type;
                if (modelActivity.typeProperties.authentication.credential) {
                    activity.authentication.credential = ReferenceAdapter.toProxyModel(
                        Metamodel.modelFor(Credential),
                        modelActivity.typeProperties.authentication.credential,
                        this.context
                    );
                }
            }

            if (GlobalVariables.enableNotebookSparkConfigOverride) {
                activity.configurationType = modelActivity.typeProperties.configurationType as A365BackendModel.ConfigurationType;
                activity.sparkConfig = toParameterClass(modelActivity.typeProperties.sparkConfig, activity);

                const sparkConfig = modelActivity.typeProperties.targetSparkConfiguration;
                if (sparkConfig != null) {
                    if (typeof sparkConfig.referenceName === 'string') {
                        activity.targetSparkConfiguration = ReferenceAdapter.toProxyModel<SparkConfiguration>(
                            Metamodel.modelFor(SparkConfiguration),
                            sparkConfig, this.context);
                    } else {
                        ValueAdapter.toModel(activity, SynapseNotebookActivity.TARGET_SPARK_CONFIGURATION, sparkConfig.referenceName);
                    }
                }
            }
        }

        if (modelActivity.linkedServiceName != null) {
            activity.linkedService = ReferenceAdapter.toProxyModel<AzureSynapseArtifactsLinkedService>(Metamodel.modelFor(AzureSynapseArtifactsLinkedService),
                modelActivity.linkedServiceName, this.context);
            if (modelActivity.linkedServiceName.parameters) {
                // Parameter value for linked service
                convertParametersToModel(activity,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    activity.linkedService,
                    activity.linkedServiceParameters,
                    modelActivity.linkedServiceName.parameters);
            }
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, SynapseNotebookActivity.MODEL));
        return activity;
    }

    private _getNotebook(activity: SynapseNotebookActivity): Model.ArtifactReference {
        const notebook = activity.notebook;
        if (!notebook) {
            const name = ValueAdapter.toResource(activity, SynapseNotebookActivity.NAME);
            return name ? {
                referenceName: name,
                type: 'NotebookReference'
            } : undefined;
        }

        if (activity.linkedService) {
            return {
                referenceName: this._toResourceWithLinkedService(notebook.name),
                type: 'NotebookReference'
            };
        }

        return ReferenceAdapter.toResource(notebook, this.context) as Model.ArtifactReference;
    }

    private _getSparkpool(activity: SynapseNotebookActivity): Model.ArtifactReference {
        const sparkPool = activity.sparkPool;
        if (!sparkPool) {
            const name = ValueAdapter.toResource(activity, SynapseNotebookActivity.SPARKPOOL);
            return name ? {
                referenceName: name,
                type: 'BigDataPoolReference'
            } : undefined;
        }

        if (activity.linkedService) {
            return {
                referenceName: this._toResourceWithLinkedService(sparkPool.name),
                type: 'BigDataPoolReference'
            };
        }

        return ReferenceAdapter.toResource(sparkPool, this.context) as Model.ArtifactReference;
    }

    private _getSparkConfiguration(activity: SynapseNotebookActivity): Model.ArtifactReference {
        const sparkConfig = activity.targetSparkConfiguration;
        if (!sparkConfig) {
            const name = ValueAdapter.toResource(activity, SynapseNotebookActivity.TARGET_SPARK_CONFIGURATION);
            return name ? {
                referenceName: name,
                type: 'SparkConfigurationReference'
            } : undefined;
        }

        if (activity.linkedService) {
            return {
                referenceName: this._toResourceWithLinkedService(sparkConfig.name),
                type: 'SparkConfigurationReference'
            };
        }

        return ReferenceAdapter.toResource(sparkConfig, this.context) as Model.ArtifactReference;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _toResourceWithLinkedService(name: string): any {
        return {
            value : name,
            type: 'Expression'
        };
    }
}
